import { z } from 'zod';
import { MailingAddressSchema } from './geo';
import { StoreSchema } from './store';
import { IncomingPhoneNumberSchema } from './twilio';
import { TimezoneOptions } from '@/types';
import { utils } from '@/helpers';

export const IndustrySchema = z.enum(['cannabis', 'brewery', 'coffee', 'nightlife', 'political', 'entertainment', 'firearms', 'other']);
export const AccountSubtypeSchema = z.enum(['retail', 'brand', 'vertical', 'sandbox', 'freemium-retail', 'freemium-brand', 'freemium-vertical']);

export const SandboxTermsSchema = z.object({
	acceptedBy: z.string().optional(),
	timestamp: z.number().optional(),
});

export const PrioritySchema = z.object({
	emails: z.array(z.string()).optional(),
	phones: z.array(z.string()).optional(),
});

export const ImpliedConsentOptsSchema = z.object({
	enabled: z.boolean().optional(),
	start: z.number().optional(),
});

export const PackageSchema = z.object({
	type: z.string(),
	priceOverride: z.number().nullable(),
});

export const PackagesSchema = z.object({
	marketing: PackageSchema,
	loyalty: PackageSchema,
	analytics: PackageSchema,
	brandAnalytics: PackageSchema,
	dataops: PackageSchema,
	support: PackageSchema,
	nativeApps: PackageSchema,
});

export const MessagingDefaultsSchema = z.object({
	mms: z.number(),
	sms: z.number(),
	email: z.number(),
	push: z.number(),
	directMail: z.number(),
	call: z.number(),
});

export const FeesV2Schema = z.object({
	address: MailingAddressSchema.optional(),
	packages: PackagesSchema,
	billingEmail: z.string(),
	name: z.string(),
	ccEmails: z.array(z.string()),
	bundle: z.string(),
	messaging: z.string(),
	messagingDefaults: MessagingDefaultsSchema,
	customOverage: z.number(),
	billingCreation: z.number(),
	contractStart: z.number(),
	termLength: z.number(),
	nativeAppStartDate: z.number(),
	nativeAppTermLength: z.number(),
	coupon: z.string(),
	additionalCoupons: z.array(z.string()).optional(),
	customDomainCount: z.number(),
	ecommLicenseCharge: z.number(),
	ecommStores: z.number(),
	marketSpotlightStateCount: z.number(),
	marketSpotlightCountryCount: z.number(),
	marketSpotlightAliasCount: z.number(),
	contactCount: z.number(),
	chargePerSignupFormBoost: z.number(),
	signupFormBoosts: z.number(),
	chargePerAudienceBoost: z.number(),
	audienceBoosts: z.number(),
});

export const FeesSchema = z.object({
	['0to10k']: z.number().nullable(),
	['10to20k']: z.number().nullable(),
	['20to40k']: z.number().nullable(),
	['40to100k']: z.number().nullable(),
	['100to200k']: z.number().nullable(),
	['200k']: z.number().nullable(),
	['400k']: z.number().nullable(),
	address: MailingAddressSchema.optional(),
	chargePerCPMAds: z.number().nullable(),
	chargePerSentEmail: z.number().nullable(),
	chargePerCallMinute: z.number().nullable(),
	chargePerSentBrowser: z.number().nullable(),
	chargePerSentPush: z.number().nullable(),
	chargePerSentSnailMail: z.number().nullable(),
	chargePerSentSMS: z.number().nullable(),
	chargePerSentMMS: z.number().nullable(),
	chargePerReceivedSMS: z.number().nullable(),
	chargePerReceivedMMS: z.number().nullable(),
	chargePerNumber: z.number().nullable(),
	chargePerStoreLicense: z.number().nullable(),
	chargePerStoreManagedService: z.number().nullable(),
	premiumSupportCharge: z.number().nullable(),
	premiumSupportPlusCharge: z.number().nullable(),
	brandLicenseCharge: z.number().nullable(),
	ecommLicenseCharge: z.number().nullable(),
	ecommStores: z.number().nullable(),
	nativeAppCharge: z.number().nullable(),
	chargePerDomain: z.number().nullable(),
	nativeAppStartDate: z.number().nullable(),
	flatFee: z.number().nullable(),
	isFranchise: z.boolean().nullable(),
	transactionPricing: z.boolean().nullable(),
	firstMonthTransactions: z.number().nullable(),
	firstMonthStores: z.number().nullable(),
	billingEmail: z.string(),
	coupon: z.string(),
	name: z.string(),
	currency: z.string(),
	ccEmails: z.array(z.string()),
	planType: z.number(),
	billingCreation: z.number(),
	contractStart: z.number(),
	termLength: z.number(),
});

export const AddressSchema = z.object({
	zip: z.string().optional(),
	city: z.string().optional(),
	state: z.string().optional(),
	country: z.string().optional(),
});

export const OnboardingSchema = z.object({
	steps: z.record(z.boolean()).optional(),
	connections: z.record(z.string()).optional(),
	lastStep: z.boolean().optional(),
	firstStore: z.number().optional(),
	finishedSignup: z.number().optional(),
	finished: z.number().optional(),
});

export const SubUserSchema = z.object({
	subUsers: z.array(z.string()).optional(),
	subUserTypes: z.record(z.string()).optional(),
	allowedIPs: z.record(z.array(z.string())).optional(),
});

export const ProfileSchema = SubUserSchema.extend({
	email: z.string().email({ message: 'Invalid email address' }).optional(),
	phone: z.string().refine(
		(phone) => {
			// can be empty, but if provided must match phone REX
			if (!phone) return true;
			return utils.phoneRex.test(phone);
		},
		{ message: 'Invalid phone number' },
	),
	avatar: z.string().optional(),
	name: z.string().min(1, { message: 'Name is required' }).optional(),
	website: z.string().url({ message: 'Invalid website URL' }).optional(),
	password: z.string().optional(),
	notes: z.string().optional(),
	subType: AccountSubtypeSchema,
	memberType: z.string().optional(),
	industry: IndustrySchema,
	timezone: z.string().refine((timezone) => TimezoneOptions.some((tz) => tz.value === timezone), { message: 'Invalid timezone' }),
	cc: z.array(z.string()).optional(),
	isSub: z.boolean().optional(),
});

export const GoogleConfigSchema = z.object({
	domain: z.string().optional(),
	enabled: z.boolean().optional(),
});

export const MicrosoftConfigSchema = z.object({
	domain: z.string().optional(),
	enabled: z.boolean().optional(),
});

export const UserTypeSchema = z.enum(['advertiser', 'agency']);

export const UserSchema = z.object({
	onboarding: OnboardingSchema.optional(),
	message2FA: z.boolean().optional(),
	messageDOB: z.boolean().optional(),
	fees: FeesSchema.optional(),
	feesV2: FeesV2Schema.optional(),
	pendingFeesV2: FeesV2Schema.optional(),
	excludedFromCmpApprovalNotifs: z.array(z.string()),
	prevShortyPrefixes: z.array(z.string()).optional(),
	requirePhoneCaptcha: z.boolean().optional(),
	address: AddressSchema.optional(),
	isIO: z.boolean().optional(),
	billing: z.record(z.any()).optional(), // TODO: Add billing.CustomerData
	metadata: z.record(z.any()).optional(),
	cndResp: z.record(z.string()).optional(),
	autoResponder: z.record(z.string()).optional(),
	profile: ProfileSchema,
	signupNum: IncomingPhoneNumberSchema.optional(),
	chargebee: z.record(z.any()).optional(), // TODO: Add customer.Customer
	rcrly: z.record(z.any()).optional(), // TODO: Add recurly.Account
	btByRegion: z.record(z.array(z.string())).optional(),
	brandAliases: z.record(z.record(z.array(z.string()))).optional(),
	allowAds: z.boolean().optional(),
	thirdPartyStores: z.record(z.array(z.number())).optional(),
	invoiceCSV: z.record(z.number()).optional(),
	reportStorage: z.record(z.string()).optional(),
	reportStoragePersona: z.record(z.string()).optional(),
	reportStorageDebug: z.record(z.string()).optional(),
	macroFallback: z.record(z.string()).optional(),
	brandOptOuts: z.array(z.string()).optional(),
	pr: PrioritySchema.optional(),
	google: GoogleConfigSchema.optional(),
	microsoft: MicrosoftConfigSchema.optional(),
	sandboxTerms: SandboxTermsSchema.optional(),
	dispenseOrgID: z.string().optional(),
	telnyxSignup: z.string().optional(),
	voiceNumber: z.string().optional(),
	salesRep: z.string().optional(),
	pointsBlacklist: z.string().optional(),
	currency: z.string().optional(),
	type: UserTypeSchema,
	postSaleMessage: z.string().optional(),
	id: z.string().optional(),
	agencyID: z.string().optional(),
	msgContainer: z.string().optional(),
	walletLoginMessage: z.string().optional(),
	adomain: z.string().optional(),
	apiKey: z.string().optional(),
	postOptinMessage: z.string().optional(),
	referredBy: z.string().optional(),
	subType: AccountSubtypeSchema,
	masterVerificationPin: z.string().optional(),
	optinMessage: z.string().optional(),
	infobipBrandID: z.string().optional(),
	infobipCmpID: z.string().optional(),
	pendingInfobipCmpID: z.string().optional(),
	optinImage: z.string().optional(),
	tcmpID: z.string().optional(),
	tbrandID: z.string().optional(),
	pos: z.string().optional(),
	pendingMessaging: z.string().optional(),
	shortyPrefix: z.string().optional(),
	lastRebirthType: z.string().optional(),
	nameMacroFallback: z.string().optional(),
	notes: z.string().optional(),
	replySTOP: z.string().optional(),
	sendgridKey: z.string().optional(),
	nickname: z.string().optional(),
	// exporters: z.record(z.any()).optional(), // TODO: Add integrations.Exporters
	// extensions: z.record(z.any()).optional(), // TODO: Add integrations.Extensions
	// importers: z.record(z.any()).optional(), // TODO: Add integrations.Importers
	invoicesList: z.array(z.record(z.any())).optional(), // TODO: Add billing.InvoiceResponseData
	stores: z.array(StoreSchema).optional(), // TODO: Add Store
	rdomains: z.array(z.string()).optional(),
	impliedConsent: ImpliedConsentOptsSchema.optional(),
	infobipCmpIDTS: z.number().optional(),
	totalRevenue: z.number().optional(),
	refreshTS: z.number().optional(),
	lastUpdated: z.number().optional(),
	lastCapture: z.number().optional(),
	lastAudienceRun: z.number().optional(),
	pointsLastAudienceRun: z.number().optional(),
	top20PercentileSpend: z.number().optional(),
	created: z.number().optional(),
	maxMTU: z.number().optional(),
	lastAccess: z.number().optional(),
	lastBrandTargetUpdate: z.number().optional(),
	lastRebirth: z.number().optional(),
	loyaltyCutoff: z.number().optional(),
	frsExport: z.number().optional(),
	lastProbation: z.number().optional(),
	lastSpotlight: z.number().optional(),
	lastUserDeletion: z.number().optional(),
	lastUserAddition: z.number().optional(),
	lastStoreDeletion: z.number().optional(),
	lastStoreAddition: z.number().optional(),
	lastTextingUpdate: z.number().optional(),
	appSettingsUpdate: z.number().optional(),
	lastInvoiceTime: z.number().optional(),
	pendingPlanRequested: z.number().optional(),
	msgingPlanRequested: z.number().optional(),
	orderPriority: z.number().optional(),
	freqCap: z.boolean().optional(),
	disableInfobip: z.boolean().optional(),
	blockDataSharing: z.boolean().optional(),
	refreshDomain: z.boolean().optional(),
	probation: z.boolean().optional(),
	alternateTelnyx: z.boolean().optional(),
	use10DLCFlow: z.boolean().optional(),
	pendingAlternate: z.boolean().optional(),
	approvedTelnyxCampaign: z.boolean().optional(),
	approvedTelnyxCampaignTS: z.number().optional(),
	lastInvoiceFreeze: z.number().optional(),
	contractStart: z.number().optional(),
	invoiceFreeze: z.boolean().optional(),
	analyzedPoints: z.boolean().optional(),
	verifiedOptin: z.boolean().optional(),
	skipProviderRestrictions: z.boolean().optional(),
	enableMessageInbox: z.boolean().optional(),
	manualNums: z.boolean().optional(),
	hidePointsInWallet: z.boolean().optional(),
	masterVerification: z.boolean(),
	active: z.boolean().optional(),
	disableBlockAdjustments: z.boolean().optional(),
	enableRelativeExpirationEarn: z.boolean().optional(),
	disableWalledGarden: z.boolean().optional(),
	utmTags: z.boolean().optional(),
	archived: z.boolean().optional(),
	disableAnonLoyalty: z.boolean().optional(),
	brandStoresForMarketAnalytics: z.boolean().optional(),
	optOutBounces: z.boolean().optional(),
	optOutSpamReports: z.boolean().optional(),
	optOutInvalidPhone: z.boolean().optional(),
	nameDOBMerge: z.boolean().optional(),
	hasCustomApp: z.boolean().optional(),
	incrText: z.boolean().optional(),
	refreshPhones: z.boolean().optional(),
	nextRefreshFree: z.boolean().optional(),
	mmsLogo: z.boolean().optional(),
	hasDynamicContent: z.boolean().optional(),
	hasDynamicContentViaAPI: z.boolean().optional(),
	hasDynamicAbandonCart: z.boolean().optional(),
	disableImplicitEmail: z.boolean().optional(),
	disableImplicitEmailTS: z.number().optional(),
	disableImplicitVoice: z.boolean(),
	showPerStoreBilling: z.boolean().optional(),
	allowCollectibleCodes: z.boolean().optional(),
	disableShorty: z.boolean().optional(),
	blockAgencyBilling: z.boolean().optional(),
	optInAttempts: z.number().optional(),
	settingsVersion: z.number().optional(),
	loyaltyAppVersion: z.number().optional(),
	signupFormsVersion: z.number().optional(),
	allowStaffToEditPersonaTraits: z.boolean().optional(),
	allowBanned: z.boolean().optional(),
});

export const AgencySchema = z.object({
	advertiserIDs: z.array(z.string()).optional(),
	user: UserSchema.optional(),
	id: z.string(),
});
